<template>
  <!--
  ==========================================================================
  General
  ==========================================================================
  -->
  <div class="h-100 d-flex">
    <div class="flex-grow-1 border-right border-secondary">
      <base-status-diagram
        :value="value"
        :errors="filterErrors('statuses')"
        :selected="selectedStatus || selectedArrow"
        @status="selectStatus"
        @arrow="selectArrow"
        @none="selectNone"
      />
    </div>
    <div
      v-if="selectedStatus || selectedArrow"
      class="flex-grow-1 d-flex flex-column bg-dark"
      style="max-width: 50%;"
    >
      <div class="p-1">
        <b-button
          block
          variant="cancel"
          class="border-white"
          @click.prevent="selectNone"
        >
          {{ $t("common.close") }}
        </b-button>
      </div>
      <base-status-form
        class="m-1 mt-0 bg-white p-3 h-100 scrollable"
        :failure-status-id="failureStatusId"
        v-if="selectedStatus"
        :value="selectedStatus"
        :has-side-status="hasSideStatus(selectedStatus)"
        :errors="errorsFor(selectedStatus)"
        :disabled="disabled"
        @input="statusChanged($event)"
        @remove="remove(selectedStatus)"
        @add-side-status="addSideStatus(selectedStatus)"
        @add-status-before="addStatusBefore(selectedStatus)"
        @add-status-after="addStatusAfter(selectedStatus)"
        @manage-tags="manageTags(selectedStatus)"
      />

      <full-page-modal
        ref="selectedStatusTagsModal"
        hide-header-close
        header-bg-variant="primary"
        header-class="text-white"
        body-class="p-0"
        ok-only
        :title="selectedStatus && selectedStatus.label"
      >
        <base-status-tag-alternatives-form
          v-if="selectedStatus"
          :value="selectedStatus"
          :disabled="disabled"
          :errors="errorsFor(selectedStatus)"
          @input="statusChanged($event)"
        />
        <!--
        <template slot="modal-footer">
          <slot name="search-buttons"></slot>
        </template>
        -->
      </full-page-modal>

      <base-arrow-form
        class="m-1 mt-0 bg-white p-3 h-100 scrollable"
        v-if="selectedArrow"
        :value="selectedArrow"
        :disabled="disabled"
        :errors="errorsFor(selectedArrow)"
        @input="arrowChanged($event)"
      />
    </div>
  </div>
</template>
<script>
import EditorMixin from "vue-template/common/mixins/EditorMixin";
import BaseStatusDiagram from "./BaseStatusDiagram";
import sortBy from "lodash/sortBy";
import findIndex from "lodash/findIndex";
//import max from "lodash/max";
import { uuid } from "core/utils";
import BaseStatusForm from "./BaseStatusForm";
import BaseArrowForm from "./BaseArrowForm";
import BaseStatusTagAlternativesForm from "./BaseStatusTagAlternativesForm";

export default {
  mixins: [EditorMixin],

  components: {
    BaseStatusDiagram,
    BaseStatusForm,
    BaseArrowForm,
    BaseStatusTagAlternativesForm
  },

  data() {
    return {
      selectedStatus: null,
      selectedArrow: null,
      errorCounter: 0
    };
  },

  watch: {
    errors(newValue) {
      if (!newValue) {
        this.errorCounter = 0;
      } else {
        this.errorCounter = newValue.filter(e =>
          e.path.startsWith("statuses[")
        ).length;
      }
    }
  },

  computed: {
    sortedStatuses() {
      return sortBy(this.value.statuses, ["rowPosition", "colPosition"]);
    },

    failureStatusId() {
      const s = this.value.statuses.find(n => n.type === "FAILURE");
      return s && s.id;
    }
  },

  methods: {
    errorsFor(s) {
      if (s.toStatusId && s.fromStatusId) {
        const nodeIndex = findIndex(
          this.value.statuses,
          x => x.id === s.fromStatusId
        );
        const node = this.value.statuses[nodeIndex];
        const arcIndex = findIndex(
          node.outgoingArrows,
          a => a.toStatusId === s.toStatusId
        );
        const prefix = `statuses[${nodeIndex}].outgoingArrows[${arcIndex}]`;
        const errors = this.filterErrors(prefix);
        if (!errors) {
          return undefined;
        }
        return errors.map(e => ({
          message: e.message,
          path: e.path.substring(prefix.length + 1)
        }));
      } else {
        const index = findIndex(this.value.statuses, x => x.id === s.id);
        const prefix = `statuses[${index}]`;
        const errors = this.filterErrors(prefix);
        if (!errors) {
          return undefined;
        }
        return errors.map(e => ({
          message: e.message,
          path: e.path.substring(prefix.length + 1)
        }));
      }
    },

    statusChanged(s) {
      const statuses = [...this.value.statuses];
      const index = statuses.indexOf(this.selectedStatus);
      statuses[index] = s;
      this.selectedStatus = s;
      this.$emit("input", { ...this.value, statuses });
    },

    arrowChanged(a) {
      const statuses = [...this.value.statuses];
      const index = findIndex(statuses, s => s.id === a.fromStatusId);
      const newStatus = { ...a.fromStatus };
      const outgoingArrows = [...newStatus.outgoingArrows];
      const arrowIndex = findIndex(
        outgoingArrows,
        x => x.toStatusId === a.toStatusId
      );
      outgoingArrows[arrowIndex] = {
        ...outgoingArrows[arrowIndex],
        label: a.label
      };
      newStatus.outgoingArrows = outgoingArrows;
      statuses[index] = newStatus;
      this.selectedArrow = a;
      this.$emit("input", { ...this.value, statuses });
    },

    addStatusBefore(s) {
      console.log("Add status before");

      const statuses = [...this.value.statuses];

      const previousStatus = statuses.find(
        x =>
          x.rowPosition === s.rowPosition - 1 && x.colPosition === s.colPosition
      );

      const previousStatusArrow = previousStatus.outgoingArrows.find(
        a => a.toStatusId === s.id
      );

      const newStatusId = uuid();

      for (let i in statuses) {
        const oldStatus = statuses[i];
        const replacedStatus = { ...oldStatus };

        if (previousStatus.id === replacedStatus.id) {
          // Rimuove il vecchio link dallo stato precedente
          replacedStatus.outgoingArrows = replacedStatus.outgoingArrows.filter(
            a => a.toStatusId !== s.id
          );
          // Aggiunge il nuovo link allo stato precedente
          replacedStatus.outgoingArrows.push({
            fromStatusId: previousStatus.id,
            toStatusId: newStatusId,
            label: previousStatusArrow.label
          });
        }
        if (replacedStatus.rowPosition >= s.rowPosition) {
          replacedStatus.rowPosition++;
        }
        statuses[i] = replacedStatus;
      }

      const newStatus = {
        id: newStatusId,
        label: s.label + " [Precedente]", // TODO
        type: "INTERMEDIATE",
        primaryActor: [],
        maxDays: 10,
        helpText: null,
        rowPosition: s.rowPosition,
        colPosition: s.colPosition,
        deletable: true,
        outgoingArrows: [
          {
            fromStatusId: newStatusId,
            toStatusId: s.id,
            label: previousStatusArrow.label
          }, // TODO
          {
            fromStatusId: newStatusId,
            toStatusId: this.failureStatusId,
            label: "Annulla"
          } // TODO
        ],
        tagAlternatives: []
      };

      statuses.push(newStatus);
      this.selectedStatus = newStatus;

      this.$emit("input", { ...this.value, statuses });
    },

    addStatusAfter(s) {
      console.log("Add status after");

      const statuses = [...this.value.statuses];

      const nextStatus = statuses.find(
        x =>
          x.rowPosition === s.rowPosition + 1 && x.colPosition === s.colPosition
      );

      const nextStatusArrow = s.outgoingArrows.find(
        a => a.toStatusId === nextStatus.id
      );

      const newStatusId = uuid();

      for (let i in statuses) {
        const oldStatus = statuses[i];
        const replacedStatus = { ...oldStatus };

        if (s.id === replacedStatus.id) {
          // Rimuove il vecchio link verso lo stato successivo
          replacedStatus.outgoingArrows = replacedStatus.outgoingArrows.filter(
            a => a.toStatusId !== nextStatus.id
          );
          // Aggiunge il nuovo link verso il nuovo stato
          replacedStatus.outgoingArrows.push({
            fromStatusId: s.id,
            toStatusId: newStatusId,
            label: nextStatusArrow.label
          });
        }

        if (replacedStatus.rowPosition > s.rowPosition) {
          replacedStatus.rowPosition++;
        }
        statuses[i] = replacedStatus;
      }

      const newStatus = {
        id: newStatusId,
        label: s.label + " [Successivo]", // TODO
        type: "INTERMEDIATE",
        primaryActor: [],
        maxDays: 10,
        helpText: null,
        rowPosition: s.rowPosition + 1,
        colPosition: s.colPosition,
        deletable: true,
        outgoingArrows: [
          {
            fromStatusId: newStatusId,
            toStatusId: nextStatus.id,
            label: nextStatusArrow.label
          }, // TODO
          {
            fromStatusId: newStatusId,
            toStatusId: this.failureStatusId,
            label: "Annulla"
          } // TODO
        ],
        tagAlternatives: []
      };

      statuses.push(newStatus);
      this.selectedStatus = newStatus;

      this.$emit("input", { ...this.value, statuses });
    },

    hasSideStatus(s) {
      const rowPosition = s.rowPosition;
      const colPosition = s.colPosition + 1;
      return !!this.value.statuses.find(
        x => x.rowPosition === rowPosition && x.colPosition === colPosition
      );
    },

    /**
     * Uno stato laterale è uno stato di controllo a cui si arriva da uno
     * stato principale e da cui si ritorna allo stato da cui si è arrivati.
     * L'aggiunta di uno stato laterale comporta la creazione del nuovo stato
     * e delle relative frecce da e verso lo stato principale relativo.
     */
    addSideStatus(s) {
      const rowPosition = s.rowPosition;
      const colPosition = s.colPosition + 1;
      const id = uuid();
      const newStatus = {
        id,
        label: s.label + "[Integrazione]", // TODO
        type: "INTERMEDIATE",
        primaryActor: [],
        maxDays: 10,
        helpText: null,
        rowPosition,
        colPosition,
        deletable: true,
        outgoingArrows: [
          { fromStatusId: id, toStatusId: s.id, label: "Reinvia" }, // TODO
          {
            fromStatusId: id,
            toStatusId: this.failureStatusId,
            label: "Annulla"
          } // TODO
        ],
        tagAlternatives: []
      };
      const statuses = [...this.value.statuses, newStatus];
      const index = findIndex(statuses, x => x.id === s.id);
      statuses[index] = {
        ...s,
        outgoingArrows: [
          ...s.outgoingArrows,
          { fromStatusId: s.id, toStatusId: id, label: "Richiedi integrazione" } // TODO
        ]
      };

      this.selectedStatus = newStatus;
      this.$emit("input", { ...this.value, statuses });
    },

    /**
     * Uno stato laterale è uno stato di controllo a cui si arriva da uno
     * stato principale e da cui si ritorna allo stato da cui si è arrivati.
     * La rimozione di uno stato laterale comporta l'eliminazione dello stato
     * stesso e delle frecce che portano ad esso.
     */
    removeSideStatus(s) {
      const id = s.id;
      const statuses = this.value.statuses
        .filter(s => s.id !== id)
        .map(s => ({
          ...s,
          outgoingArrows: s.outgoingArrows.filter(a => a.toStatusId !== id)
        }));
      return statuses;
    },

    /**
     * Uno stato principale è uno stato che si trova lungo il percorso ideale
     * dallo stato iniziale a quello finale di successo. La sua rimozione
     * implica l'eliminazione di eventuali stati laterali connessi e l'unione
     * del percorso pre e post stato.
     */
    removeMainStatus(s) {
      // Trova l'eventuale stato laterale
      const sideStatus = this.value.statuses.find(
        t =>
          t.colPosition === s.colPosition + 1 && t.rowPosition === s.rowPosition
      );

      // Se è presente uno stato laterale, lo rimuove (comprese frecce)
      let statuses = sideStatus
        ? this.removeSideStatus(sideStatus)
        : this.value.statuses;

      // Trova lo stato precedente
      const previousStatus = statuses.find(
        t =>
          t.colPosition === s.colPosition && t.rowPosition === s.rowPosition - 1
      );

      // Trova lo stato successivo
      const nextStatus = statuses.find(
        t =>
          t.colPosition === s.colPosition && t.rowPosition === s.rowPosition + 1
      );

      // Freccia verso lo stato successivo (esiste sicuramente)
      const arrowToNextStatus = s.outgoingArrows.find(
        a => a.toStatusId === nextStatus.id
      );

      // Freccia verso lo stato precedente (potrebbe non esistere)
      const arrowToPreviousStatus = s.outgoingArrows.find(
        a => a.toStatusId === previousStatus.id
      );

      // Freccia dallo stato successivo (potrebbe non esistere)
      const arrowFromNextStatus = nextStatus.outgoingArrows.find(
        a => a.toStatusId === s.id
      );

      statuses = statuses
        .filter(t => t.id !== s.id) // Rimuove lo stato eliminato
        .map(t => ({
          ...t,

          // Rimuove le frecce verso lo stato eliminato
          outgoingArrows: t.outgoingArrows.filter(a => a.toStatusId !== s.id),

          // Sposta in alto tutti gli stati che si trovavano più in basso
          // dello stato eliminato
          rowPosition:
            t.rowPosition > s.rowPosition ? t.rowPosition - 1 : t.rowPosition
        }));

      // Crea la nuova freccia dallo stato precedente a quello successivo
      statuses
        .find(t => t.id === previousStatus.id)
        .outgoingArrows.push({
          fromStatusId: previousStatus.id,
          toStatusId: nextStatus.id,
          label: arrowToNextStatus.label
        });

      // Se era presente una freccia verso lo stato precedente o una
      // dallo stato successivo, ricrea la freccia indietro
      let arrowToPreviousStatusLabel = null;
      if (arrowToPreviousStatus) {
        arrowToPreviousStatusLabel = arrowToPreviousStatus.label;
      } else if (arrowFromNextStatus) {
        arrowToPreviousStatusLabel = arrowFromNextStatus.label;
      }

      if (arrowToPreviousStatusLabel) {
        statuses
          .find(t => t.id === nextStatus.id)
          .outgoingArrows.push({
            fromStatusId: nextStatus.id,
            toStatusId: previousStatus.id,
            label: arrowToPreviousStatusLabel
          });
      }

      return statuses;
    },

    manageTags(/*s*/) {
      this.$refs.selectedStatusTagsModal.show();
    },

    remove(s) {
      if (!confirm(`Vuoi veramente eliminare lo stato '${s.label}'?`)) {
        return;
      }

      if (s.colPosition === 2) {
        const statuses = this.removeSideStatus(s);
        this.selectedStatus = null;
        this.$emit("input", { ...this.value, statuses });
      } else {
        const statuses = this.removeMainStatus(s);
        this.selectedStatus = null;
        this.$emit("input", { ...this.value, statuses });
      }
    },

    selectStatus(status) {
      //console.log("Status selected", status);
      this.selectedStatus = status;
      this.selectedArrow = null;
    },

    selectArrow({ fromStatus, toStatus, arrow }) {
      //console.log("Arrow selected", { fromStatus, toStatus, arrow });
      this.selectedStatus = null;
      this.selectedArrow = { ...arrow, fromStatus, toStatus };
    },

    selectNone() {
      //console.log('Nothing selected');
      this.selectedStatus = null;
      this.selectedArrow = null;
    }
  }
};
</script>
